/**
 * Central vehicle image URL helper – shared by web and mobile.
 * Supports Supabase Storage (and future CDN) with optional transform (width, height, quality).
 *
 * TODO (future CDN switch): To use Cloudflare/Cloudinary instead of Supabase origin:
 * - Web: set baseUrl in getVehicleImageUrl (e.g. from env VITE_IMAGE_CDN_URL) when present.
 * - Mobile: set baseUrl in getVehicleImageUrl (e.g. from EXPO_PUBLIC_IMAGE_CDN_URL) when present.
 * - Keep same getVehicleImageUrlCore(pathOrUrl, options, baseUrl) contract; only baseUrl changes.
 */

export const VEHICLE_IMAGE_PLACEHOLDER = '/placeholder.svg';

export interface GetVehicleImageUrlOptions {
  width?: number;
  height?: number;
  quality?: number;
}

/**
 * Parses pathOrUrl and returns { bucket, path } for Supabase Storage, or null if external/invalid.
 */
function parseSupabaseStoragePath(
  pathOrUrl: string,
  baseUrl: string
): { bucket: string; path: string } | null {
  const trimmed = pathOrUrl.trim();
  if (!trimmed) return null;

  const normalizedBase = baseUrl.replace(/\/$/, '');

  // Full Supabase URL: .../storage/v1/object/public/BUCKET/PATH or .../render/image/public/BUCKET/PATH or signed
  if (trimmed.startsWith('http') && trimmed.includes(normalizedBase) && trimmed.includes('/storage/')) {
    const match = trimmed.match(/\/storage\/v1\/(?:object|render\/image)\/public\/([^/?]+)\/(.+?)(?:\?|$)/);
    if (match) {
      return { bucket: match[1], path: decodeURIComponent(match[2].split('?')[0]) };
    }
    const signMatch = trimmed.match(/\/storage\/v1\/object\/sign\/([^/?]+)\/(.+?)(?:\?|$)/);
    if (signMatch) {
      return { bucket: signMatch[1], path: decodeURIComponent(signMatch[2].split('?')[0]) };
    }
  }

  // Path-like: "vehicleId/file.jpg", "car-images/...", "vehicle-photos/...", etc.
  if (!trimmed.startsWith('http')) {
    let path = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\//i;
    const isUuidPath = uuidPattern.test(path);

    let bucket = 'vehicles';
    if (path.startsWith('car-images/')) {
      bucket = 'car-images';
      path = path.replace(/^car-images\//, '');
    } else if (path.startsWith('vehicle-photos/')) {
      bucket = 'vehicle-photos';
      path = path.replace(/^vehicle-photos\//, '');
    } else if (path.startsWith('user_documents/')) {
      bucket = 'user_documents';
      path = path.replace(/^user_documents\//, '');
    } else if (path.startsWith('check-in-out/')) {
      bucket = 'check-in-out';
      path = path.replace(/^check-in-out\//, '');
    } else if (isUuidPath) {
      bucket = 'car-images';
    } else if (!path.startsWith('images/') && !path.includes('/')) {
      path = `images/${path}`;
    } else if (!path.startsWith('images/') && path.includes('/') && !isUuidPath) {
      path = `images/${path}`;
    }

    if (!path.includes('.')) return null;
    if (trimmed === 'bookings' || trimmed === 'vehicles' || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
      return null;
    }
    return { bucket, path };
  }

  return null;
}

/**
 * Returns true if the given URL/path is from Supabase Storage (for the given baseUrl).
 */
export function isSupabaseStorageUrl(pathOrUrl: string, baseUrl: string): boolean {
  if (!pathOrUrl?.trim()) return false;
  const trimmed = pathOrUrl.trim();
  if (trimmed.startsWith('http') && trimmed.includes(baseUrl) && trimmed.includes('/storage/')) {
    return true;
  }
  if (!trimmed.startsWith('http') && trimmed.includes('.')) {
    return true;
  }
  return false;
}

/**
 * Core: build vehicle image URL. baseUrl is the Supabase project URL (no trailing slash).
 * Future CDN: pass Cloudflare/Cloudinary base URL here in one place per app.
 */
export function getVehicleImageUrlCore(
  pathOrUrl: string | undefined | null,
  options: GetVehicleImageUrlOptions | undefined,
  baseUrl: string
): string {
  if (!pathOrUrl || pathOrUrl.trim() === '') {
    return VEHICLE_IMAGE_PLACEHOLDER;
  }

  // Return app placeholder as-is; do not treat it as a Supabase path (would 404)
  const trimmed = pathOrUrl.trim();
  if (trimmed === VEHICLE_IMAGE_PLACEHOLDER || trimmed === '/placeholder.svg') {
    return VEHICLE_IMAGE_PLACEHOLDER;
  }

  const parsed = parseSupabaseStoragePath(pathOrUrl, baseUrl);

  if (!parsed) {
    // External URL (not Supabase) – return as-is, no transform
    if (pathOrUrl.trim().startsWith('http')) {
      return pathOrUrl.trim();
    }
    return VEHICLE_IMAGE_PLACEHOLDER;
  }

  const { bucket, path } = parsed;
  const encodedPath = path.split('/').map(segment => encodeURIComponent(segment)).join('/');

  // Intentionally ignore width/height/quality: Supabase Image Transform bills per
  // distinct origin image (Pro quota 100/month). Catalog size exceeds that quota.
  // Keep the options param for API compatibility; serve original public URLs.
  void options;

  return `${baseUrl}/storage/v1/object/public/${bucket}/${encodedPath}`;
}
